import { DatePipe } from '@angular/common';
import {
  Component,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule, MatDateRangePicker } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { ICampaignAnalyticsFilter } from '@pages/campaigns/campaign-details/campaign-details.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { DatePickerUtilitiesService } from '@utilities/date-picker-utilities.service';
import { endOfDay, isBefore, isEqual, max, startOfDay } from 'date-fns';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Subscription } from 'rxjs';

@Component({
  imports: [
    DatePipe,
    IconComponent,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    ReactiveFormsModule,
    TranslateModule,
    NgxSkeletonLoaderModule,
    MatErrorComponent,
  ],
  providers: [provideNativeDateAdapter()],
  selector: 'app-start-end-date-picker',
  templateUrl: './start-end-date-picker.component.html',
})
export class StartEndDatePickerComponent implements OnDestroy {
  public readonly startDateControl = input.required<FormControl<Date | null>>();
  public readonly endDateControl = input.required<FormControl<Date | null>>();

  public readonly initialEndDate = input<Date | null>(null);
  public readonly initialStartDate = input<Date | null>(null);
  public readonly startDateLabel = input<string>();
  public readonly endDateLabel = input<string>();
  public readonly loading = input<boolean>(false);
  public readonly minDate = input<Date | null>(null);
  public readonly maxDate = input<Date | null>(null);

  public readonly dateRangeChanged = output<Pick<ICampaignAnalyticsFilter, 'endDate' | 'startDate'>>();

  public readonly anyPickersOpen = signal<boolean>(false);

  private readonly startRangePicker = viewChild.required<MatDateRangePicker<Date>>('startRangePicker');
  private readonly endDatePicker = viewChild.required<MatDatepicker<Date>>('endDatePicker');

  public readonly datePickerUtilities = inject(DatePickerUtilitiesService);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private isSyncingPicker = false;
  private lastEmittedDateRange: Pick<ICampaignAnalyticsFilter, 'endDate' | 'startDate'> | null = null;
  private initialDatesConsumed = false;

  private startPickerSubscription: Subscription | null = null;
  private endPickerSubscription: Subscription | null = null;

  // The controls are bound as they are (never wrapped in a group of this component's own), so they keep
  // their parent form and the caller's valueChanges. Their values are read from them on every use -
  // a caller may write them without emitting, which a cached copy would miss and later write back.
  constructor() {
    effect(() => {
      const startDateControl = this.startDateControl();
      const endDateControl = this.endDateControl();

      if (!startDateControl || !endDateControl) {
        return;
      }

      untracked(() => this.setupPickerSubscriptions());
    });

    effect(() => {
      const initialStartDate = this.initialStartDate();
      const initialEndDate = this.initialEndDate();

      untracked(() => this.applyInitialDates(initialStartDate, initialEndDate));
    });
  }

  public openStartPicker(event: Event): void {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.startDateControl().disabled) {
      return;
    }

    if (this.endDatePicker()) {
      this.endDatePicker().close();
    }

    this.anyPickersOpen.set(true);

    requestAnimationFrame(() => {
      this.syncPickerSelections();
      this.startRangePicker().open();
    });
  }

  public openEndPicker(event: Event): void {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.endDateControl().disabled) {
      return;
    }

    if (this.startRangePicker()) {
      this.startRangePicker().close();
    }

    this.anyPickersOpen.set(true);

    requestAnimationFrame(() => {
      this.syncPickerSelections();
      this.endDatePicker().open();
    });
  }

  // A saved start may lie before the campaign; the end calendar still offers no day before either.
  public getEndMinDate(): Date | null {
    const startDate = this.startDateControl().value;
    const minDate = this.minDate();

    return startDate && minDate ? max([startDate, minDate]) : (startDate ?? minDate);
  }

  public onEndDateChange(date: Date | null): void {
    const validEndDate: Date | null = this.datePickerUtilities.getValidEndDate(date, this.startDateControl().value);

    if (!validEndDate) {
      return;
    }

    // A pick in the calendar is a user edit; the picker's own syncs write without marking it.
    this.endDateControl().setValue(endOfDay(validEndDate));
    this.endDateControl().markAsDirty();
  }

  public ngOnDestroy(): void {
    this.startPickerSubscription?.unsubscribe();
    this.endPickerSubscription?.unsubscribe();
  }

  // Only the first values count, as before: they seed the controls once.
  private applyInitialDates(initialStartDate: Date | null, initialEndDate: Date | null): void {
    if (this.initialDatesConsumed) {
      return;
    }

    this.initialDatesConsumed = true;

    if (initialEndDate) {
      this.endDateControl().setValue(new Date(initialEndDate), { emitEvent: false });
    }

    if (initialStartDate) {
      this.startDateControl().setValue(new Date(initialStartDate), { emitEvent: false });
    }

    if (initialStartDate || initialEndDate) {
      this.ensureEndDateWhenOnlyStartSelected();
    }
  }

  private setupPickerSubscriptions(): void {
    this.startPickerSubscription?.unsubscribe();
    this.endPickerSubscription?.unsubscribe();

    if (this.startRangePicker()) {
      this.startPickerSubscription = this.startRangePicker().closedStream.subscribe(() => this.onPickerClosed());
    }

    if (this.endDatePicker()) {
      this.endPickerSubscription = this.endDatePicker().closedStream.subscribe(() => this.onPickerClosed());
    }
  }

  private onPickerClosed(): void {
    this.extendPickedEndDate();
    this.ensureEndDateWhenOnlyStartSelected();
    this.removeFloatingLabel();

    requestAnimationFrame(() => {
      this.syncPickerSelections();
    });
  }

  // A day picked in the calendar arrives at midnight and covers its whole day. Only on a pick: the range
  // input re-emits the end on every validator change, and a time the caller set is kept.
  private extendPickedEndDate(): void {
    const endDate = this.endDateControl().value;

    if (endDate && isEqual(endDate, startOfDay(endDate))) {
      this.endDateControl().setValue(endOfDay(endDate));
    }
  }

  private syncPickerSelections(): void {
    if (this.isSyncingPicker) {
      return;
    }

    const startPicker = this.startRangePicker();
    const endPicker = this.endDatePicker();

    if (startPicker?.opened || endPicker?.opened) {
      return;
    }

    this.isSyncingPicker = true;

    // Read before the pickers' select() calls below, which clear the end for a moment.
    const startDate = this.startDateControl().value;
    const endDate = this.endDateControl().value;

    setTimeout(() => {
      if (startPicker && !startPicker.opened) {
        if (startDate) {
          startPicker.select(startDate);
        }

        if (endDate) {
          startPicker.select(endDate);
        }
      }

      if (endPicker && !endPicker.opened && endDate) {
        endPicker.select(endDate);
      }

      this.startDateControl().setValue(startDate);
      this.endDateControl().setValue(endDate);

      this.emitDateRange(startDate, endDate);

      this.isSyncingPicker = false;
    });
  }

  private emitDateRange(startDate: Date | null, endDate: Date | null): void {
    if (endDate === this.lastEmittedDateRange?.endDate && startDate === this.lastEmittedDateRange?.startDate) {
      return;
    }

    this.lastEmittedDateRange = { endDate, startDate };

    this.dateRangeChanged.emit({
      endDate: endOfDay(endDate ?? new Date()),
      startDate: startOfDay(startDate ?? new Date()),
    });
  }

  private ensureEndDateWhenOnlyStartSelected(): void {
    const startDate = this.startDateControl().value;
    const endDate = this.endDateControl().value;

    if (startDate && (!endDate || isBefore(startOfDay(endDate), startOfDay(startDate)))) {
      this.endDateControl().setValue(endOfDay(startDate));
      this.syncPickerSelections();
    }
  }

  private removeFloatingLabel(): void {
    if (this.startDateControl().value || this.endDateControl().value) {
      return;
    }

    setTimeout(() => {
      this.elementRef.nativeElement.querySelectorAll('label').forEach((label) => {
        label.classList.remove('mdc-floating-label--float-above');
      });

      this.anyPickersOpen.set(false);
    }, 130);
  }
}
